// ---------------------------------------------------------------------------
// BrowserTargetingForm (OPUS-D10 / feature #70).
//
// Operator-facing form for `/api/v1/campaign-browser-targeting`:
//   - campaign ID text input
//   - allow / deny mode toggle
//   - browser multi-select as chip cluster
//   - save + clear actions
//
// Design constraints per CLAUDE.md dashboard theme:
//   - light theme only
//   - emerald -> sky brand gradient on the heading
//   - Inter font (handled globally via tailwind)
//   - 6-8px rounded corners, zinc borders, subtle shadow
//   - no emojis, no competitor names
//
// The form is controlled React state (no react-hook-form) so we stay within
// the scaffold's dep-lock. Validation uses the hand-rolled zod-shaped
// `browserTargetingFormSchema.safeParse` from `./api`. TanStack Query wires
// fetch / mutate; Sonner surfaces success + error toasts.
// ---------------------------------------------------------------------------

import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api-client";
import {
	BROWSER_CODES,
	BROWSER_LABELS,
	type BrowserCode,
	type TargetingMode,
	apiListToFormSelection,
	browserTargetingFormSchema,
	browserTargetingQueryKeys,
	deleteBrowserTargeting,
	fetchBrowserTargeting,
	saveBrowserTargeting,
} from "./api";

// ---------------------------------------------------------------------------
// Small visual helpers — kept colocated to preserve the "3 owned files" rule.
// ---------------------------------------------------------------------------

type FieldErrors = Record<string, string>;

const FIELD_BORDER =
	"rounded-[6px] border bg-[color:var(--color-surface)] px-3 py-2 text-sm text-[color:var(--color-text)] placeholder:text-[color:var(--color-text-faint)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/30";

const PRIMARY_BUTTON =
	"inline-flex items-center justify-center rounded-[8px] px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60";

const SECONDARY_BUTTON =
	"inline-flex items-center justify-center rounded-[8px] border px-4 py-2 text-sm font-medium text-[color:var(--color-text)] transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BrowserTargetingFormProps {
	/** Optional pre-filled campaign ID when deep-linked from a campaign detail page. */
	initialCampaignId?: string;
}

export function BrowserTargetingForm({
	initialCampaignId = "",
}: BrowserTargetingFormProps) {
	const queryClient = useQueryClient();

	const [campaignId, setCampaignId] = useState(initialCampaignId);
	const [lookupId, setLookupId] = useState(initialCampaignId.trim());
	const [mode, setMode] = useState<TargetingMode>("allow");
	const [selected, setSelected] = useState<BrowserCode[]>([]);
	const [errors, setErrors] = useState<FieldErrors>({});

	// --- remote: fetch existing rule when a lookupId is set ------------------
	const query = useQuery({
		queryKey: browserTargetingQueryKeys.byCampaign(lookupId || "__empty__"),
		queryFn: () => fetchBrowserTargeting(lookupId),
		enabled: lookupId.length > 0,
	});

	// Sync form state when the server row arrives.
	useEffect(() => {
		if (!query.data) return;
		const mapped = apiListToFormSelection(query.data.browsers);
		setMode(mapped.mode);
		setSelected(mapped.selected);
	}, [query.data]);

	// Reset selection to an empty "allow" default when the server reports no row.
	useEffect(() => {
		if (query.isSuccess && query.data == null) {
			setMode("allow");
			setSelected([]);
		}
	}, [query.isSuccess, query.data]);

	// --- remote: mutations ---------------------------------------------------
	const saveMutation = useMutation({
		mutationFn: saveBrowserTargeting,
		onSuccess: (row) => {
			queryClient.setQueryData(
				browserTargetingQueryKeys.byCampaign(row.campaign_id),
				row,
			);
			toast.success("Browser targeting saved.");
		},
		onError: (err) => {
			const message =
				err instanceof ApiError
					? err.message
					: "Unable to save browser targeting.";
			toast.error(message);
		},
	});

	const clearMutation = useMutation({
		mutationFn: deleteBrowserTargeting,
		onSuccess: (_v, campaignIdArg) => {
			queryClient.setQueryData(
				browserTargetingQueryKeys.byCampaign(campaignIdArg),
				null,
			);
			setMode("allow");
			setSelected([]);
			toast.success("Rule cleared. All browsers allowed again.");
		},
		onError: (err) => {
			const message =
				err instanceof ApiError
					? err.message
					: "Unable to clear the rule.";
			toast.error(message);
		},
	});

	const isBusy =
		query.isFetching || saveMutation.isPending || clearMutation.isPending;

	// --- handlers ------------------------------------------------------------
	function toggleBrowser(code: BrowserCode) {
		setSelected((prev) =>
			prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
		);
	}

	function handleLookup() {
		const next = campaignId.trim();
		setLookupId(next);
		setErrors({});
	}

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const result = browserTargetingFormSchema.safeParse({
			campaignId,
			mode,
			selected,
		});
		if (!result.success) {
			setErrors(result.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		setLookupId(result.data.campaignId);
		saveMutation.mutate({
			campaignId: result.data.campaignId,
			mode: result.data.mode,
			selected: result.data.selected,
		});
	}

	function handleClear() {
		const trimmed = campaignId.trim();
		if (!trimmed) {
			setErrors({ campaignId: "Campaign ID is required." });
			return;
		}
		clearMutation.mutate(trimmed);
	}

	const canSubmit = !isBusy && campaignId.trim().length > 0;
	const ruleExists = query.data != null;

	return (
		<form
			onSubmit={handleSubmit}
			noValidate
			className="flex flex-col gap-6 rounded-[8px] border bg-[color:var(--color-surface)] p-6 shadow-[var(--shadow-brand-card)]"
			style={{ borderColor: "var(--color-border-strong)" }}
		>
			<CampaignIdField
				value={campaignId}
				onChange={setCampaignId}
				onLookup={handleLookup}
				error={errors.campaignId}
				isLoading={query.isFetching && lookupId.length > 0}
				exists={ruleExists}
			/>

			<ModeToggle value={mode} onChange={setMode} error={errors.mode} />

			<BrowserChipGroup
				mode={mode}
				selected={selected}
				onToggle={toggleBrowser}
				error={errors.selected}
			/>

			<div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4"
				style={{ borderColor: "var(--color-border)" }}>
				<SummaryLine mode={mode} selected={selected} />
				<div className="flex gap-2">
					<button
						type="button"
						className={SECONDARY_BUTTON}
						style={{ borderColor: "var(--color-border-strong)" }}
						disabled={!ruleExists || isBusy}
						onClick={handleClear}
					>
						{clearMutation.isPending ? "Clearing..." : "Clear rule"}
					</button>
					<button
						type="submit"
						className={PRIMARY_BUTTON}
						style={{
							backgroundImage:
								"linear-gradient(90deg, var(--color-brand-emerald) 0%, var(--color-brand-sky) 100%)",
						}}
						disabled={!canSubmit}
					>
						{saveMutation.isPending ? "Saving..." : "Save targeting"}
					</button>
				</div>
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Subcomponents — local-only, not exported.
// ---------------------------------------------------------------------------

interface CampaignIdFieldProps {
	value: string;
	onChange: (v: string) => void;
	onLookup: () => void;
	error?: string;
	isLoading: boolean;
	exists: boolean;
}

function CampaignIdField({
	value,
	onChange,
	onLookup,
	error,
	isLoading,
	exists,
}: CampaignIdFieldProps) {
	return (
		<div className="flex flex-col gap-1.5">
			<label
				htmlFor="campaign-id"
				className="text-sm font-medium text-[color:var(--color-text)]"
			>
				Campaign ID
			</label>
			<div className="flex flex-wrap items-center gap-2">
				<input
					id="campaign-id"
					name="campaignId"
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onBlur={onLookup}
					placeholder="e.g. cmp_123"
					autoComplete="off"
					className={`${FIELD_BORDER} w-72`}
					style={{ borderColor: "var(--color-border-strong)" }}
					aria-invalid={Boolean(error)}
					aria-describedby={error ? "campaign-id-error" : undefined}
				/>
				<button
					type="button"
					onClick={onLookup}
					className={SECONDARY_BUTTON}
					style={{ borderColor: "var(--color-border-strong)" }}
				>
					{isLoading ? "Loading..." : "Load existing"}
				</button>
				{!isLoading && value.trim().length > 0 ? (
					<span className="text-xs text-[color:var(--color-text-muted)]">
						{exists ? "Rule exists — editing." : "No rule yet — creating new."}
					</span>
				) : null}
			</div>
			{error ? (
				<p id="campaign-id-error" className="text-xs text-[color:var(--color-danger)]">
					{error}
				</p>
			) : null}
		</div>
	);
}

interface ModeToggleProps {
	value: TargetingMode;
	onChange: (m: TargetingMode) => void;
	error?: string;
}

function ModeToggle({ value, onChange, error }: ModeToggleProps) {
	return (
		<div className="flex flex-col gap-1.5">
			<span className="text-sm font-medium text-[color:var(--color-text)]">
				Mode
			</span>
			<div
				role="radiogroup"
				aria-label="Targeting mode"
				className="inline-flex w-fit rounded-[8px] border p-0.5"
				style={{ borderColor: "var(--color-border-strong)" }}
			>
				{(["allow", "deny"] as const).map((m) => {
					const active = value === m;
					return (
						<button
							key={m}
							type="button"
							role="radio"
							aria-checked={active}
							onClick={() => onChange(m)}
							className="rounded-[6px] px-4 py-1.5 text-sm font-medium transition-colors"
							style={
								active
									? {
											backgroundImage:
												"linear-gradient(90deg, var(--color-brand-emerald) 0%, var(--color-brand-sky) 100%)",
											color: "white",
										}
									: { color: "var(--color-text-muted)" }
							}
						>
							{m === "allow" ? "Allow list" : "Deny list"}
						</button>
					);
				})}
			</div>
			<p className="text-xs text-[color:var(--color-text-muted)]">
				{value === "allow"
					? "Only the selected browsers will match this campaign."
					: "Traffic from the selected browsers will be blocked."}
			</p>
			{error ? (
				<p className="text-xs text-[color:var(--color-danger)]">{error}</p>
			) : null}
		</div>
	);
}

interface BrowserChipGroupProps {
	mode: TargetingMode;
	selected: BrowserCode[];
	onToggle: (code: BrowserCode) => void;
	error?: string;
}

function BrowserChipGroup({
	mode,
	selected,
	onToggle,
	error,
}: BrowserChipGroupProps) {
	const selectedSet = useMemo(() => new Set(selected), [selected]);
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="text-sm font-medium text-[color:var(--color-text)]">
				Browsers
			</legend>
			<p className="text-xs text-[color:var(--color-text-muted)]">
				{mode === "allow"
					? "Tap the browsers to include."
					: "Tap the browsers to block."}
			</p>
			<div className="flex flex-wrap gap-2">
				{BROWSER_CODES.map((code) => {
					const active = selectedSet.has(code);
					return (
						<button
							key={code}
							type="button"
							onClick={() => onToggle(code)}
							aria-pressed={active}
							className="rounded-[6px] border px-3 py-1.5 text-sm transition-colors"
							style={
								active
									? {
											borderColor: "var(--color-brand-emerald)",
											backgroundColor: "rgba(16,185,129,0.08)",
											color: "var(--color-text)",
										}
									: {
											borderColor: "var(--color-border-strong)",
											color: "var(--color-text-muted)",
										}
							}
						>
							{BROWSER_LABELS[code]}
						</button>
					);
				})}
			</div>
			{error ? (
				<p className="text-xs text-[color:var(--color-danger)]">{error}</p>
			) : null}
		</fieldset>
	);
}

interface SummaryLineProps {
	mode: TargetingMode;
	selected: BrowserCode[];
}

function SummaryLine({ mode, selected }: SummaryLineProps) {
	if (selected.length === 0) {
		return (
			<span className="text-xs text-[color:var(--color-text-muted)]">
				Select at least one browser to save.
			</span>
		);
	}
	const names = selected.map((c) => BROWSER_LABELS[c]).join(", ");
	return (
		<span className="text-xs text-[color:var(--color-text-muted)]">
			{mode === "allow" ? "Allowing" : "Blocking"}: {names}
		</span>
	);
}

export default BrowserTargetingForm;
